import { Link } from 'react-router-dom';
import { FaGraduationCap, FaBook, FaTrophy, FaLaptop, FaFlask, FaMedal, FaCompass, FaCheckCircle, FaWhatsapp, FaStar, FaPhoneAlt, FaFire, FaBullseye } from 'react-icons/fa';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

const features = [
  { icon: <FaGraduationCap size={32} />, title: 'Expert Faculty', desc: 'Experienced teachers with proven track records in NEET, IIT-JEE & board exams.', color: '#1E3A8A' },
  { icon: <FaBook size={32} />, title: 'Study Materials', desc: 'Comprehensive notes and PDFs for all subjects, available for free download.', color: '#F59E0B' },
  { icon: <FaTrophy size={32} />, title: 'Top Results', desc: '500+ students cleared NEET, IIT-JEE, and AMU in the last 3 years.', color: '#22C55E' },
  { icon: <FaLaptop size={32} />, title: 'Online Classes', desc: 'Live and recorded sessions accessible anytime, anywhere on any device.', color: '#7C3AED' },
];

const WA_MSG = encodeURIComponent('Hello, I want to book a counseling session');
const WA_LINK = `https://wa.me/917417652434?text=${WA_MSG}`;

const courses = [
  { icon: <FaFlask size={32} />, title: 'Science Stream (IX–XII)',    desc: 'Physics, Chemistry, Maths + Biology with concept-based teaching and regular tests.', tag: 'School', color: '#0EA5E9' },
  { icon: <FaMedal size={32} />, title: 'Competitive Exams',   desc: 'NEET, IIT-JEE, AMU, JMI, CPT preparation with mock tests.', tag: 'Entrance', color: '#F59E0B' },
  { icon: <FaCompass size={32} />, title: 'Career Counseling',   desc: 'Expert guidance after 10th, 12th & Graduation — free one-to-one sessions.', tag: 'Free', color: '#22C55E' },
];

// ── STUDENT REVIEWS ──────────────────────────────────────────
// To update: copy real reviews from Google Maps or JustDial
// and replace the text, name, date fields below.
// ─────────────────────────────────────────────────────────────
const testimonials = [
  {
    name: 'Mohammad Asad',
    source: 'Google Review',
    rating: 5,
    date: 'March 2019',
    text: 'SBS Institute mein padhna mera best decision tha. Physics aur Chemistry ke teachers bahut clearly samjhate hain. Mera board result 89% aaya. Bahut shukriya SBS team ko.',
    tag: 'Class 12 Student',
    avatar: 'MA',
    avatarColor: '#1E3A8A',
  },
  {
    name: 'Priya Verma',
    source: 'Google Review',
    rating: 5,
    date: 'January 2024',
    text: 'Career counseling session ne meri life change kar di. Sir ne mujhe B.Pharma ke baare mein guide kiya aur ab main Subharti University mein admission le chuki hoon. Highly recommended!',
    tag: 'Career Counseling',
    avatar: 'PV',
    avatarColor: '#15803D',
  },
  {
    name: 'Rahul Kumar',
    source: 'Google Review',
    rating: 5,
    date: 'February 2024',
    text: 'AMU entrance ke liye SBS se better koi option nahi. Notes detailed hain, mock tests regular hote hain aur teachers hamesha available rehte hain. Main select ho gaya — thank you SBS!',
    tag: 'AMU Entrance',
    avatar: 'RK',
    avatarColor: '#7C3AED',
  },
  {
    name: 'Anjali Singh',
    source: 'Google Review',
    rating: 5,
    date: 'December 2023',
    text: 'Fees reasonable hai aur quality top class. Maths ke doubts jo school mein clear nahi hote the, yahan ek session mein clear ho gaye. Class 10 mein 92% aaye. Parents bhi bahut khush hain.',
    tag: 'Class 10 Board',
    avatar: 'AS',
    avatarColor: '#C2410C',
  },
  {
    name: 'Riya Dhiman',
    source: 'Google Review',
    rating: 5,
    date: 'November 2018',
    text: 'JMI entrance ki preparation ke liye SBS best hai. Study material aur regular practice tests se meri preparation solid ho gayi. Result mein select hua. Dil se shukriya.',
    tag: 'JMI Entrance',
    avatar: 'RA',
    avatarColor: '#0369A1',
  },
  {
    name: 'Neha Sharma',
    source: 'Google Review',
    rating: 5,
    date: 'October 2023',
    text: 'PDF notes aur online materials bahut useful hain. Teachers kabhi bhi doubt solve karte hain — WhatsApp pe bhi. Ek saal mein mera confidence aur marks dono badh gaye.',
    tag: 'Class 11 Student',
    avatar: 'NS',
    avatarColor: '#B45309',
  },
];

export default function Home() {
  const { ref: statsRef, inView: statsInView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  return (
    <>
      {/* ── Hero ── */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge"><FaFire size={14} style={{ marginRight: '0.3rem' }} /> New Batch Starting Soon — Limited Seats!</div>
          <h1>
            <span>SBS Institute</span><br />
            of Coaching
          </h1>
          <p>
            Classes for IX–XII, Graduation &amp; Competitive Exams<br />
            Expert Faculty &bull; Proven Results &bull; Affordable Fees
          </p>
          <div className="hero-btns">
            <Link to="/contact" className="btn-primary">Enroll Now →</Link>
            <Link to="/courses" className="btn-outline">View Courses</Link>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="stats-section" ref={statsRef}>
        <div className="stats-grid">
          {[
            { num: 1500, suffix: '+', label: 'Students Enrolled' },
            { num: 50, suffix: '+', label: 'Expert Teachers' },
            { num: 15, suffix: '+', label: 'Years Experience' },
            { num: 95, suffix: '%', label: 'Success Rate' },
          ].map(({ num, suffix, label }) => (
            <div className="stat-item" key={label}>
              <h3>
                {statsInView ? (
                  <>
                    <CountUp
                      start={0}
                      end={num}
                      duration={2.5}
                      separator=","
                      decimals={0}
                      useEasing={true}
                      easingFn={(t, b, c, d) => {
                        // easeOutExpo
                        return c * (-Math.pow(2, -10 * t / d) + 1) + b;
                      }}
                    />
                    {suffix}
                  </>
                ) : (
                  `0${suffix}`
                )}
              </h3>
              <p>{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: 'clamp(3rem, 6vw, 5rem) 1.2rem', maxWidth: 1200, margin: '0 auto' }}>
        <div className="page-title">
          <h2>Why Choose <span>SBS Institute?</span></h2>
          <p>Everything you need to succeed in one place</p>
          <div className="accent-line" />
        </div>
        <div className="cards-grid" style={{ marginTop: '2.5rem' }}>
          {features.map(({ icon, title, desc, color }) => (
            <div className="card" key={title}>
              <div className="card-icon" style={{ color }}>{icon}</div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Courses Preview ── */}
      <section style={{ background: 'var(--bg)', padding: 'clamp(3rem, 6vw, 5rem) 1.2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="page-title">
            <h2>Our <span>Courses</span></h2>
            <p>Comprehensive coaching for every academic level</p>
            <div className="accent-line" />
          </div>
          <div className="cards-grid" style={{ marginTop: '2.5rem' }}>
            {courses.map(({ icon, title, desc, tag, color }) => (
              <div className="card" key={title} style={{
                borderTop: `3px solid ${tag === 'Free' ? 'var(--green)' : 'var(--blue-mid)'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div className="card-icon" style={{ marginBottom: 0, color }}>{icon}</div>
                  <span className="card-tag" style={tag === 'Free' ? {
                    background: '#F0FDF4', color: 'var(--green-dark)', border: '1px solid #BBF7D0',
                  } : {}}>{tag}</span>
                </div>
                <h3 style={tag === 'Free' ? { color: 'var(--green-dark)' } : {}}>{title}</h3>
                <p>{desc}</p>
                {tag === 'Free' && (
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      marginTop: '1rem', background: '#25D366', color: '#fff',
                      padding: '0.5rem 1.1rem', borderRadius: 8,
                      fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none',
                    }}
                  >
                    <FaPhoneAlt size={12} /> Book Free Session
                  </a>
                )}
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/courses" className="btn-gold">Explore All Courses →</Link>
          </div>
        </div>
      </section>

      {/* ── Career Counseling Teaser ── */}
      <section style={{ padding: '5rem 1.2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            background: 'linear-gradient(135deg, #1E3A8A 0%, #1d4ed8 100%)',
            borderRadius: 16,
            padding: 'clamp(1.8rem, 4vw, 3.5rem) clamp(1.2rem, 4vw, 3rem)',
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
          }}>
            {/* Content */}
            <div>
              <div style={{
                display: 'inline-block',
                background: 'rgba(245,158,11,0.2)',
                border: '1px solid rgba(245,158,11,0.5)',
                color: '#FCD34D',
                padding: '0.3rem 1rem',
                borderRadius: 50,
                fontSize: '0.8rem',
                fontWeight: 600,
                marginBottom: '1rem',
              }}>🆓 Completely Free</div>
              <h2 style={{ color: '#fff', fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 800, marginBottom: '0.8rem', lineHeight: 1.2 }}>
                Career Counseling for Students
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: 1.7 }}>
                Guidance for students after 10th, 12th, and Graduation. Choose the right career path with our expert mentors.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                {[
                  'Help students choose the right career path',
                  'Guidance for B.Tech, BCA, BBA, B.Com, Polytechnic, B.Pharma',
                  'College selection support',
                  'Future planning and mentorship',
                  'One-to-one expert guidance',
                ].map(point => (
                  <div key={point} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'rgba(255,255,255,0.88)', fontSize: '0.9rem' }}>
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#22C55E',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.7rem', fontWeight: 800, flexShrink: 0, color: '#fff',
                    }}><FaCheckCircle size={12} /></span>
                    {point}
                  </div>
                ))}
              </div>
            </div>
            {/* CTA */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.8rem' }}>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>Talk to an expert today — No fees. No obligation.</p>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  background: '#25D366',
                  color: '#fff',
                  padding: '0.9rem 1.8rem',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  boxShadow: '0 6px 20px rgba(37,211,102,0.45)',
                }}
              >
                <FaWhatsapp size={20} />
                Book Free Counseling
              </a>
            </div>
          </div>
        </div>
      </section>

      

      {/* ── Associated Universities ── */}
      <section style={{ background: 'var(--white)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: 'clamp(3rem, 6vw, 4.5rem) 1.2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="page-title">
            <h2>Our Associated <span>Universities</span></h2>
            <p>We provide guidance and counseling for students in collaboration with top institutions</p>
            <div className="accent-line" />
          </div>

          {/* Scrolling marquee strip */}
          <div className="uni-track-wrapper">
            <div className="uni-track">
              {/* Doubled for seamless loop */}
              {[
                { name: 'Shobhit University',          abbr: 'SU',  color: '#1E3A8A' },
                { name: 'Neelkanth University',         abbr: 'NU',  color: '#7C3AED' },
                { name: 'Subharti University',          abbr: 'SBU', color: '#0369A1' },
                { name: 'Dewan Group of Institutions',  abbr: 'DGI', color: '#B45309' },
                { name: 'IIMT University',              abbr: 'IIMT',color: '#15803D' },
                { name: 'Shobhit University',          abbr: 'SU',  color: '#1E3A8A' },
                { name: 'Neelkanth University',         abbr: 'NU',  color: '#7C3AED' },
                { name: 'Subharti University',          abbr: 'SBU', color: '#0369A1' },
                { name: 'Dewan Group of Institutions',  abbr: 'DGI', color: '#B45309' },
                { name: 'IIMT University',              abbr: 'IIMT',color: '#15803D' },
              ].map((u, i) => (
                <div className="uni-card" key={i}>
                  <div className="uni-logo" style={{ background: u.color }}>{u.abbr}</div>
                  <span className="uni-name">{u.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Static grid cards below */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
            gap: '1rem',
            marginTop: '2.5rem',
          }}>
            {[
              { name: 'Shobhit University',         abbr: 'SU',   color: '#1E3A8A', bg: '#EFF6FF', border: '#BFDBFE',  loc: 'Meerut, UP' },
              { name: 'Neelkanth University',        abbr: 'NU',   color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE',  loc: 'Roorkee, UK' },
              { name: 'Subharti University',         abbr: 'SBU',  color: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD',  loc: 'Meerut, UP' },
              { name: 'Dewan Group of Institutions', abbr: 'DGI',  color: '#B45309', bg: '#FFFBEB', border: '#FDE68A',  loc: 'Meerut, UP' },
              { name: 'IIMT University',             abbr: 'IIMT', color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0',  loc: 'Meerut, UP' },
            ].map(({ name, abbr, color, bg, border, est, loc }) => (
              <div key={name} style={{
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 14,
                padding: '1.5rem 1.2rem',
                textAlign: 'center',
                transition: 'transform 0.25s, box-shadow 0.25s',
                cursor: 'default',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 28px ${border}`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {/* Logo circle */}
                <div style={{
                  width: 64, height: 64,
                  borderRadius: '50%',
                  background: color,
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: abbr.length > 2 ? '0.75rem' : '1.1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1rem',
                  boxShadow: `0 4px 14px ${border}`,
                  letterSpacing: '0.5px',
                }}>{abbr}</div>
                <div style={{ fontWeight: 700, color: color, fontSize: '0.95rem', lineHeight: 1.3, marginBottom: '0.5rem' }}>{name}</div>
                <div style={{ color: 'var(--gray)', fontSize: '0.78rem' }}>{loc}</div>
                <div style={{ color: 'var(--gray)', fontSize: '0.75rem', marginTop: '0.2rem' }}>{est}</div>
                {/* Association badge */}
                <div style={{
                  display: 'inline-block',
                  marginTop: '0.8rem',
                  background: color,
                  color: '#fff',
                  padding: '0.2rem 0.7rem',
                  borderRadius: 20,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                }}>✓ Associated</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={{ background: 'var(--bg)', padding: 'clamp(3rem, 6vw, 5rem) 1.2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Header */}
          <div className="page-title">
            <h2>What Our <span>Students Say</span></h2>
            <p>Real reviews from Google & JustDial — shared by our students</p>
            <div className="accent-line" />
          </div>

          {/* Overall rating bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '1.5rem', flexWrap: 'wrap',
            background: 'var(--white)', border: '1px solid var(--border)',
            borderRadius: 16, padding: '1.5rem',
            margin: '2rem auto', maxWidth: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3.5rem', fontWeight: 800, color: 'var(--dark)', lineHeight: 1 }}>4.9</div>
              <div style={{ fontSize: '1.4rem', color: '#F59E0B', margin: '0.3rem 0' }}>★★★★★</div>
              <div style={{ color: 'var(--gray)', fontSize: '0.82rem' }}>Overall Rating</div>
            </div>
            <div style={{ width: 1, height: 60, background: 'var(--border)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {[[5,'★★★★★', 85],[4,'★★★★☆', 12],[3,'★★★☆☆', 3]].map(([n, stars, pct]) => (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--gray)', width: 8 }}>{n}</span>
                  <span style={{ fontSize: '0.75rem', color: '#F59E0B' }}>{stars}</span>
                  <div style={{ width: 100, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: '#F59E0B', borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>{pct}%</span>
                </div>
              ))}
            </div>
            <div style={{ width: 1, height: 60, background: 'var(--border)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
              <a
                href="https://www.google.com/maps/search/SBS+Institute+of+Coaching"
                target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  background: '#fff', border: '1.5px solid #DADCE0',
                  borderRadius: 8, padding: '0.5rem 1rem',
                  fontWeight: 600, fontSize: '0.82rem', color: '#3C4043',
                  textDecoration: 'none', transition: 'box-shadow 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                View on Google
              </a>
              <span style={{ color: 'var(--gray)', fontSize: '0.75rem' }}>50+ reviews</span>
            </div>
          </div>

          {/* Review cards grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
            gap: '1.2rem',
            marginTop: '1.5rem',
          }}>
            {testimonials.map(({ name, source, rating, date, text, tag, avatar, avatarColor }) => (
              <div key={name} style={{
                background: 'var(--white)',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                display: 'flex', flexDirection: 'column', gap: '0.8rem',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(30,58,138,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; }}
              >
                {/* Top row: avatar + name + source */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: avatarColor, color: '#fff',
                    fontWeight: 800, fontSize: '0.85rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>{avatar}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: 'var(--dark)', fontSize: '0.95rem' }}>{name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.15rem' }}>
                      {/* Source badge */}
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 600,
                        background: source === 'Google Review' ? '#EFF6FF' : '#FFF7ED',
                        color: source === 'Google Review' ? '#1E3A8A' : '#C2410C',
                        border: `1px solid ${source === 'Google Review' ? '#BFDBFE' : '#FED7AA'}`,
                        padding: '0.1rem 0.5rem', borderRadius: 20,
                      }}>
                        {source === 'Google Review' ? '🔵 Google' : '🟠 JustDial'}
                      </span>
                      <span style={{ color: 'var(--gray)', fontSize: '0.72rem' }}>{date}</span>
                    </div>
                  </div>
                </div>

                {/* Star rating */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} style={{ color: i < rating ? '#F59E0B' : '#E5E7EB', fontSize: '1rem' }}><FaStar /></span>
                  ))}
                </div>

                {/* Review text */}
                <p style={{
                  color: 'var(--gray)', fontSize: '0.88rem',
                  lineHeight: 1.7, flex: 1,
                  fontStyle: 'italic',
                }}>
                  "{text}"
                </p>

                {/* Tag */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  background: 'var(--blue-pale)', color: 'var(--blue)',
                  border: '1px solid #BFDBFE',
                  padding: '0.2rem 0.7rem', borderRadius: 20,
                  fontSize: '0.72rem', fontWeight: 600, alignSelf: 'flex-start',
                }}>🎓 {tag}</div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section style={{
        background: 'linear-gradient(135deg, var(--blue) 0%, #1d4ed8 100%)',
        padding: 'clamp(3rem, 6vw, 5rem) 1.2rem',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.4)',
            color: 'var(--gold-light)', padding: '0.35rem 1rem', borderRadius: 50,
            fontSize: '0.82rem', fontWeight: 600, marginBottom: '1.2rem',
          }}><FaBullseye size={14} style={{ marginRight: '0.3rem' }} /> Limited Seats Available</div>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--white)', marginBottom: '1rem' }}>
            Ready to Start Your Journey?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '2rem', fontSize: '1.05rem' }}>
            Join thousands of successful students. New batch starting soon!
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary">Get in Touch →</Link>
            <Link to="/courses" className="btn-outline">Browse Courses</Link>
          </div>
        </div>
      </section>
    </>
  );
}
